import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { authApiService } from "../../../../api/api";
import { IOrderShipped, OrderStatus } from "../../../../types/order.type";
import {
  IPhoneOrder,
  IPhoneOrderUpdateDto,
} from "../../../../types/phoneOrder.type";
import { createInvoice } from "../../../../utils/createInvoce";
import { toDatetimeLocalValue } from "../../../../utils/formatDate";
import {
  phoneOrderFirstsStatusSelector,
  phoneOrderSecondStatusSelector,
  phoneOrderThirdStatusSelector,
} from "../../../../utils/createSelector";
import { getPaymentStatus } from "../../../../utils/getPaymentStatus";
import BlueButton from "../../../buttons/BlueButton/BlueButton";
import ModalBottomButtons from "../buttons/ModakBottomButtons/ModalBottomButtons";
import EditInput from "../inputs/EditInput/EditInput";
import SelectInput, { SelectOption } from "../inputs/SelectInput/SelectInput";
import ViewInput from "../inputs/ViewInput/ViewInput";
import styles from "./styles.module.scss";

interface Props {
  order: IPhoneOrder;
  onClose: () => void;
  onApply: () => void;
}

const ViewPhoneOrderForm: React.FC<Props> = (props) => {
  const [ttn, setTtn] = useState<string>("");
  const [invoice, setInvoice] = useState<string>(props.order.invoice ?? "");
  const [orderDateLocal, setOrderDateLocal] = useState<string>(
    toDatetimeLocalValue(props.order.createdAt)
  );
  const [note, setNote] = useState<string>(props.order.note ?? "");
  const [clientFullName, setClientFullName] = useState<string>(
    props.order.client.fullName ?? ""
  );
  const [clientPhone, setClientPhone] = useState<string>(
    props.order.client.phone ?? ""
  );
  const [addressLine1, setAddressLine1] = useState<string>(
    props.order.adressLine1 ?? ""
  );
  const [addressLine2, setAddressLine2] = useState<string>(
    props.order.adressLine2 ?? ""
  );
  const [city, setCity] = useState<string>(props.order.city ?? "");
  const [stateVal, setStateVal] = useState<string>(props.order.state ?? "");
  const [postalCode, setPostalCode] = useState<string>(
    props.order.postalCode ?? ""
  );
  const [country, setCountry] = useState<string>(props.order.country ?? "");
  const [status, setStatus] = useState<SelectOption>({
    value: props.order.status,
    label: getPaymentStatus(props.order.status),
  });

  useEffect(() => {
    setInvoice(props.order.invoice ?? "");
    setOrderDateLocal(toDatetimeLocalValue(props.order.createdAt));
    setNote(props.order.note ?? "");
    setClientFullName(props.order.client.fullName ?? "");
    setClientPhone(props.order.client.phone ?? "");
    setAddressLine1(props.order.adressLine1 ?? "");
    setAddressLine2(props.order.adressLine2 ?? "");
    setCity(props.order.city ?? "");
    setStateVal(props.order.state ?? "");
    setPostalCode(props.order.postalCode ?? "");
    setCountry(props.order.country ?? "");
    setStatus({
      value: props.order.status,
      label: getPaymentStatus(props.order.status),
    });
  }, [props.order]);

  const firstStatuses = [
    OrderStatus.CANCELED.toString(),
    OrderStatus.PAID.toString(),
    OrderStatus.PENDING.toString(),
  ];

  const secondStatuses = [
    OrderStatus.SHIPPED.toString(),
    OrderStatus.DELIVERED.toString(),
  ];

  const onChangeStatus = async () => {
    try {
      if (
        props.order.status !== OrderStatus.SHIPPED &&
        status.value === OrderStatus.SHIPPED
      ) {
        if (!ttn) {
          toast.error("TTN is required");
          return;
        }
        const body: IOrderShipped = {
          ttn,
        };
        const response = await authApiService.shippedPhoneOrder(
          props.order.id,
          body
        );
        if (response) {
          props.onApply();
          props.onClose();
        }
      } else {
        const body: IPhoneOrderUpdateDto = {
          status: status.value,
        };

        const response = await authApiService.updatePhoneOrder(
          props.order.id,
          body
        );

        if (response) {
          props.onApply();
          props.onClose();
        }
      }
    } catch (err: any) {
      console.error(err);
      toast.error(err.message);
    }
  };

  const onSaveBasicFields = async () => {
    if (!orderDateLocal) {
      toast.error("Вкажіть дату замовлення");
      return;
    }
    try {
      const createdAt = new Date(orderDateLocal).toISOString();
      const body: IPhoneOrderUpdateDto = {
        invoice: invoice.trim(),
        createdAt,
        note: note.trim(),
        clientFullName: clientFullName.trim(),
        clientPhone: clientPhone.trim(),
        adressLine1: addressLine1,
        adressLine2: addressLine2,
        city,
        state: stateVal,
        postalCode,
        country,
      };
      await authApiService.updatePhoneOrder(props.order.id, body);
      toast.success("Зміни збережено");
      props.onApply();
    } catch (err: any) {
      console.error(err);
      toast.error(
        err.response?.data?.message || err.message || "Не вдалося зберегти"
      );
    }
  };

  return (
    <div>
      <div className={styles.form}>
        <div className={styles.metaEditSection}>
          <EditInput
            title="Номер інвойса"
            value={invoice}
            setValue={setInvoice}
            isRequired={false}
          />
          <div className={styles.datetimeRow}>
            <label htmlFor="crm-order-created-at" className={styles.datetimeLabel}>
              Дата замовлення
            </label>
            <input
              id="crm-order-created-at"
              type="datetime-local"
              className={styles.datetimeInput}
              value={orderDateLocal}
              onChange={(e) => setOrderDateLocal(e.target.value)}
            />
          </div>
          <EditInput
            title="Ім'я клієнта"
            value={clientFullName}
            setValue={setClientFullName}
            isRequired={false}
          />
          <EditInput
            title="Телефон"
            value={clientPhone}
            setValue={setClientPhone}
            isRequired={false}
          />
          <EditInput
            title="Адреса (рядок 1)"
            value={addressLine1}
            setValue={setAddressLine1}
            isRequired={false}
          />
          <EditInput
            title="Адреса (рядок 2)"
            value={addressLine2}
            setValue={setAddressLine2}
            isRequired={false}
          />
          <div className={styles.infoContainer}>
            <EditInput
              title="Місто"
              value={city}
              setValue={setCity}
              isRequired={false}
            />
            <EditInput
              title="Область / регіон"
              value={stateVal}
              setValue={setStateVal}
              isRequired={false}
            />
          </div>
          <div className={styles.infoContainer}>
            <EditInput
              title="Індекс"
              value={postalCode}
              setValue={setPostalCode}
              isRequired={false}
            />
            <EditInput
              title="Країна"
              value={country}
              setValue={setCountry}
              isRequired={false}
            />
          </div>
          <EditInput
            title="Примітка"
            value={note}
            setValue={setNote}
            isRequired={false}
            isBig
          />
          <BlueButton
            title="Зберегти зміни"
            onClick={onSaveBasicFields}
            isBigButton
          />
        </div>
        <BlueButton
          title="Open invoice"
          onClick={() => createInvoice(props.order)}
          isBigButton
        />
        <div className={styles.selectContainer}>
          {props.order.status === OrderStatus.DELIVERED ? (
            <ViewInput
              title="status"
              value={getPaymentStatus(props.order.status)}
            />
          ) : (
            <>
              <SelectInput
                title="Status"
                value={status}
                values={
                  firstStatuses.includes(props.order.status)
                    ? phoneOrderFirstsStatusSelector
                    : secondStatuses.includes(props.order.status)
                    ? phoneOrderThirdStatusSelector
                    : phoneOrderSecondStatusSelector
                }
                onChange={(value) => setStatus(value)}
              />
              {props.order.status !== OrderStatus.SHIPPED &&
                status.value === OrderStatus.SHIPPED && (
                  <EditInput
                    title="TTN"
                    value={ttn}
                    setValue={(value) => setTtn(value)}
                    isRequired
                  />
                )}
              {props.order.status !== status.value && (
                <BlueButton
                  title="Change status"
                  onClick={onChangeStatus}
                  isBigButton
                />
              )}
            </>
          )}
        </div>
        {props.order.ttn && <ViewInput title="TTN" value={props.order.ttn} />}
        <ViewInput
          title="Email"
          value={
            props.order.client.email ? props.order.client.email : "empty"
          }
        />
        <ViewInput title="Type of order" value={props.order.type} />
        {props.order.customPrice != null && (
          <ViewInput
            title="Custom price"
            value={`${
              typeof props.order.customPrice === "number"
                ? props.order.customPrice.toFixed(2)
                : Number(props.order.customPrice).toFixed(2)
            } €`}
          />
        )}
        {props.order.orderProducts.map((item) => (
          <div key={item.id}>
            <ViewInput
              title="Product"
              value={`${item.product.name}, ${item.product.capacity}`}
            />
            <div className={styles.infoContainer}>
              <ViewInput title="Quantity" value={item.quantity.toString()} />
              <ViewInput
                title="Price per unit"
                value={
                  item.pricePerUnit != null
                    ? Number(item.pricePerUnit).toString()
                    : props.order.type === "retail"
                    ? item.product.price.toString()
                    : item.product.wholeSalePrice.toString()
                }
              />
            </div>
          </div>
        ))}
        <div className={styles.infoContainer}>
          <ViewInput
            title="Total quantity"
            value={props.order.orderProducts
              .reduce((acc, item) => (acc += item.quantity), 0)
              .toString()}
          />

          <ViewInput title="Total price" value={props.order.totalAmount} />
        </div>
      </div>
      <ModalBottomButtons
        backTitle="Закрити"
        onPressBack={props.onClose}
        applyTitle=""
      />
    </div>
  );
};

export default ViewPhoneOrderForm;
